// AdminSuppliers — Supplier + Item CRUD (create/inline-edit/delete),
// stock adjustments and the danger-zone "Clear all demo data" button.

import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { NAVY, TEAL, useInv } from "./constants";
import { SEED_CATALOGUE, SEED_SUPPLIERS } from "./seed";
import { SectionHeader } from "./ui";

export function AdminSuppliers() {
  const {
    suppliers: SUPPLIERS = SEED_SUPPLIERS,
    catalogue: CATALOGUE = SEED_CATALOGUE,
    createSupplier,
    updateSupplier,
    createItem,
    updateItem,
    deleteSupplier,
    deleteItem,
    clearAllData,
    hasToken,
  } = useInv();
  const itemsPerSupplier = useMemo(() => {
    const m = {};
    for (const c of CATALOGUE) m[c.supplier] = (m[c.supplier] || 0) + 1;
    return m;
  }, [CATALOGUE]);
  const emptySupplier = { company: "", account_number: "", first: "", last: "", tel: "", email: "", address: "", city: "", province: "", postal: "", instructions: "" };

  // Persist the in-progress supplier form to sessionStorage so an iOS
  // Safari tab reload (memory pressure) or a background re-render doesn't
  // wipe what the admin already typed.
  const SUP_DRAFT_KEY = "pp-supplier-draft-v1";
  const readDraft = () => {
    try {
      const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SUP_DRAFT_KEY) : null;
      if (!raw) return null;
      const j = JSON.parse(raw);
      if (j && typeof j === "object" && j.form) return j;
    } catch { /* ignore parse errors */ }
    return null;
  };
  const initialDraft = readDraft();
  const [supForm, setSupForm] = useState(initialDraft?.form || emptySupplier);
  const [supOpen, setSupOpen] = useState(Boolean(initialDraft?.open));
  const [editingSupplierId, setEditingSupplierId] = useState(initialDraft?.editingId || null);
  const [supBusy, setSupBusy] = useState(false);
  const [supErr, setSupErr] = useState("");
  // Sync the current draft to sessionStorage on every change while the panel
  // is open. Clear it on close so we don't restore stale values later.
  useEffect(() => {
    try {
      if (supOpen) {
        sessionStorage.setItem(SUP_DRAFT_KEY, JSON.stringify({
          open: true, form: supForm, editingId: editingSupplierId,
        }));
      } else {
        sessionStorage.removeItem(SUP_DRAFT_KEY);
      }
    } catch { /* storage full / disabled */ }
  }, [supOpen, supForm, editingSupplierId]);
  const closeSupForm = () => {
    setSupOpen(false);
    setEditingSupplierId(null);
    setSupForm(emptySupplier);
    setSupErr("");
  };
  const openEditSupplier = (s) => {
    setEditingSupplierId(s.id);
    setSupForm({
      company: s.company || "",
      account_number: s.account_number || "",
      first: s.first || "",
      last: s.last || "",
      tel: s.tel || "",
      email: s.email || "",
      address: s.address || "",
      city: s.city || "",
      province: s.province || "",
      postal: s.postal || "",
      instructions: s.instructions || "",
    });
    setSupOpen(true);
    setSupErr("");
  };
  // Auto-format phone as "(XXX) XXX-XXXX" while the admin types. Strips
  // non-digits, truncates to 10 digits, then re-groups. Users can still
  // paste anything; we just present it back nicely.
  const formatTel = (raw) => {
    const d = (raw || "").replace(/\D/g, "").slice(0, 10);
    if (d.length === 0) return "";
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const saveSupplier = async () => {
    if (!supForm.company.trim()) { setSupErr("Supplier name is required"); return; }
    setSupBusy(true); setSupErr("");
    try {
      if (editingSupplierId) {
        await updateSupplier(editingSupplierId, supForm);
      } else {
        await createSupplier(supForm);
      }
      closeSupForm();
    } catch (e) {
      setSupErr(e.message || "Could not save supplier");
    } finally { setSupBusy(false); }
  };

  // Item form state (used by openAddItem/saveItem below and rendered in the
  // "Add item" panel that appears under the Suppliers section).
  const emptyItem = { name: "", code: "", description: "", category: "", pack: "Each", packQty: 1, orderPacks: 1, min: 0, max: 0, hqMin: 0, hqMax: 0 };
  // Editable category list. Seeded with sensible defaults, but the admin
  // can add/remove entries via the small "Edit list" popover next to the
  // Category field. Persisted to localStorage so it survives reloads.
  const DEFAULT_CATEGORIES = ["Screening", "Sample collection", "PPE", "Cold chain", "Sharps & disposal", "Office & admin", "Other"];
  const [categoryOptions, setCategoryOptions] = useState(() => {
    try {
      const stored = localStorage.getItem("hepcure-inv-categories");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch { /* ignore */ }
    return DEFAULT_CATEGORIES;
  });
  const persistCategories = (next) => {
    setCategoryOptions(next);
    try { localStorage.setItem("hepcure-inv-categories", JSON.stringify(next)); } catch { /* ignore */ }
  };
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [newCatInput, setNewCatInput] = useState("");
  const addCategory = () => {
    const v = newCatInput.trim();
    if (!v) return;
    if (categoryOptions.some((c) => c.toLowerCase() === v.toLowerCase())) { setNewCatInput(""); return; }
    persistCategories([...categoryOptions, v]);
    setNewCatInput("");
  };
  const removeCategory = (name) => {
    persistCategories(categoryOptions.filter((c) => c !== name));
  };
  // Inline-rename state for the manage-categories popover.
  const [editingCatName, setEditingCatName] = useState(null);
  const [editingCatDraft, setEditingCatDraft] = useState("");
  const startEditCategory = (name) => { setEditingCatName(name); setEditingCatDraft(name); };
  const cancelEditCategory = () => { setEditingCatName(null); setEditingCatDraft(""); };
  const saveEditCategory = () => {
    const oldName = editingCatName;
    const newName = editingCatDraft.trim();
    if (!oldName) { cancelEditCategory(); return; }
    if (!newName || newName === oldName) { cancelEditCategory(); return; }
    // Guard against duplicates (case-insensitive).
    if (categoryOptions.some((c) => c.toLowerCase() === newName.toLowerCase() && c !== oldName)) {
      cancelEditCategory();
      return;
    }
    persistCategories(categoryOptions.map((c) => (c === oldName ? newName : c)));
    // Keep the currently-selected item form value in sync if it referenced the renamed category.
    setItemForm((f) => (f.category === oldName ? { ...f, category: newName } : f));
    cancelEditCategory();
  };
  const [addItemForSupplier, setAddItemForSupplier] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [itemBusy, setItemBusy] = useState(false);
  const [itemErr, setItemErr] = useState("");

  // Danger zone: wipe all inventory data (production reset).
  const [clearOpen, setClearOpen] = useState(false);
  const [clearConfirm, setClearConfirm] = useState("");
  const [clearBusy, setClearBusy] = useState(false);
  const [clearErr, setClearErr] = useState("");
  const [clearDone, setClearDone] = useState(false);
  const runClearAll = async () => {
    if (clearConfirm.trim().toUpperCase() !== "WIPE") return;
    setClearBusy(true); setClearErr("");
    try {
      await clearAllData();
      setClearDone(true);
      setClearConfirm("");
      setClearOpen(false);
      setTimeout(() => setClearDone(false), 4000);
    } catch (e) {
      setClearErr(e.message || "Could not clear data");
    } finally { setClearBusy(false); }
  };

  // Track which supplier card is expanded to show its items.
  const [expandedSid, setExpandedSid] = useState(null);
  const itemsBySupplier = useMemo(() => {
    const m = {};
    for (const c of CATALOGUE) (m[c.supplier] ||= []).push(c);
    return m;
  }, [CATALOGUE]);

  const openAddItem = (sid) => {
    setEditingItemId(null);
    setAddItemForSupplier(sid);
    setExpandedSid(sid); // auto-expand the card so the form lands in context
    setItemForm(emptyItem);
    setItemErr("");
  };
  const openEditItem = (it) => {
    setEditingItemId(it.id);
    setAddItemForSupplier(it.supplier);
    setExpandedSid(it.supplier);
    setItemForm({
      name: it.name || "",
      code: it.code || "",
      description: it.description || "",
      category: it.category || "",
      pack: it.pack || "Each",
      packQty: Number(it.packQty) || 1,
      orderPacks: Number(it.orderPacks) || 1,
      min: Number(it.min) || 0,
      max: Number(it.max) || 0,
      hqMin: Number(it.hqMin) || 0,
      hqMax: Number(it.hqMax) || 0,
    });
    setItemErr("");
  };
  const closeItemForm = () => {
    setAddItemForSupplier(null);
    setEditingItemId(null);
    setItemForm(emptyItem);
    setItemErr("");
  };
  const saveItem = async () => {
    if (!itemForm.name.trim()) { setItemErr("Item name is required"); return; }
    if (!addItemForSupplier) return;
    setItemBusy(true); setItemErr("");
    try {
      if (editingItemId) {
        await updateItem(editingItemId, { ...itemForm, supplier: addItemForSupplier });
      } else {
        await createItem({ ...itemForm, supplier: addItemForSupplier });
      }
      closeItemForm();
    } catch (e) {
      setItemErr(e.message || "Could not save item");
    } finally { setItemBusy(false); }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
      <SectionHeader
        eyebrow="Admin · /users/admin"
        title="Suppliers &amp; items"
        right={
          <button
            onClick={() => { if (supOpen) { closeSupForm(); } else { setEditingSupplierId(null); setSupForm(emptySupplier); setSupOpen(true); setSupErr(""); } }}
            disabled={!hasToken}
            title={hasToken ? "" : "Sign in as admin to add suppliers"}
            data-testid="add-supplier-btn"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold text-white transition disabled:opacity-50" style={{ background: NAVY }}>
            <Plus className="h-4 w-4" /> Add supplier
          </button>
        }
      />

      {supOpen && (
        <div className="mb-4 rounded-lg border-2 p-4" style={{ borderColor: NAVY, background: `${NAVY}08` }} data-testid="supplier-form-panel">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold mb-2" style={{ color: NAVY }}>
            {editingSupplierId ? "Edit supplier" : "New supplier"}
          </p>
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <input value={supForm.company} onChange={(e) => setSupForm((f) => ({ ...f, company: e.target.value }))} placeholder="Supplier name" data-testid="supplier-company-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            <input value={supForm.account_number} onChange={(e) => setSupForm((f) => ({ ...f, account_number: e.target.value }))} placeholder="Account #" data-testid="supplier-account-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm font-mono" />
            <div className="grid grid-cols-2 gap-2">
              <input value={supForm.first} onChange={(e) => setSupForm((f) => ({ ...f, first: e.target.value }))} placeholder="First name" data-testid="supplier-first-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
              <input value={supForm.last} onChange={(e) => setSupForm((f) => ({ ...f, last: e.target.value }))} placeholder="Last name" data-testid="supplier-last-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            </div>
            <input value={supForm.tel} onChange={(e) => setSupForm((f) => ({ ...f, tel: formatTel(e.target.value) }))} placeholder="Phone" inputMode="tel" data-testid="supplier-tel-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            <input value={supForm.email} onChange={(e) => setSupForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" data-testid="supplier-email-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            <input value={supForm.address} onChange={(e) => setSupForm((f) => ({ ...f, address: e.target.value }))} placeholder="Street address" data-testid="supplier-address-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm sm:col-span-2" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:col-span-2">
              <input value={supForm.city} onChange={(e) => setSupForm((f) => ({ ...f, city: e.target.value }))} placeholder="City" data-testid="supplier-city-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
              <input value={supForm.province} onChange={(e) => setSupForm((f) => ({ ...f, province: e.target.value }))} placeholder="Province" data-testid="supplier-province-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
              <input value={supForm.postal} onChange={(e) => setSupForm((f) => ({ ...f, postal: e.target.value }))} placeholder="Postal code" data-testid="supplier-postal-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            </div>
            <input value={supForm.instructions} onChange={(e) => setSupForm((f) => ({ ...f, instructions: e.target.value }))} placeholder="Ordering instructions (optional)" data-testid="supplier-instructions-input" className="h-9 rounded-md border border-slate-300 px-2 text-sm sm:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 items-center">
            {supErr && <p className="text-xs text-red-600 mr-auto" data-testid="supplier-form-err">{supErr}</p>}
            <button onClick={closeSupForm} className="h-8 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={saveSupplier} disabled={supBusy} data-testid="save-supplier-btn" className="h-8 px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
              {supBusy ? "Saving…" : (editingSupplierId ? "Update supplier" : "Save supplier")}
            </button>
          </div>
        </div>
      )}

      {/* Supplier cards */}
      {SUPPLIERS.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500 italic">
          No suppliers yet<br />
          — click <b>Add supplier</b> above to begin.
        </div>
      )}
      <div className="space-y-3">
        {SUPPLIERS.map((s) => {
          const items = itemsBySupplier[s.id] || [];
          const expanded = expandedSid === s.id;
          const showItemFormHere = addItemForSupplier === s.id;
          const isEdit = showItemFormHere && Boolean(editingItemId);
          return (
            <div key={s.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              {/* Header row */}
              <div className="p-3 sm:p-4 space-y-3">
                <div className="min-w-0">
                  {s.account_number && (
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Account # <span className="font-mono normal-case tracking-normal text-slate-700">{s.account_number}</span>
                    </p>
                  )}
                  <p className="font-semibold text-base mt-0.5" style={{ color: NAVY }}>{s.company}</p>
                  <div className="text-xs text-slate-600 mt-0.5 leading-snug space-y-0.5">
                    {[s.first, s.last].filter(Boolean).join(" ") && (
                      <p>{[s.first, s.last].filter(Boolean).join(" ")}</p>
                    )}
                    {s.tel && (
                      <p className="tabular-nums">
                        <a href={`tel:${(s.tel || "").replace(/[^\d+]/g, "")}`} className="hover:underline" style={{ color: TEAL }} data-testid={`supplier-tel-link-${s.id}`}>
                          {s.tel}
                        </a>
                      </p>
                    )}
                    {s.email && (
                      <p className="break-all">
                        <a href={`mailto:${s.email}`} className="hover:underline" style={{ color: TEAL }} data-testid={`supplier-email-link-${s.id}`}>
                          {s.email}
                        </a>
                      </p>
                    )}
                  </div>
                  {(s.address || s.city || s.province || s.postal) && (
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                      {s.address && <div>{s.address}</div>}
                      {(s.city || s.province || s.postal) && (
                        <div>{[s.city, [s.province, s.postal].filter(Boolean).join("  ")].filter(Boolean).join(", ")}</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-nowrap">
                  <button
                    onClick={() => setExpandedSid(expanded ? null : s.id)}
                    data-testid={`view-items-btn-${s.id}`}
                    title={expanded ? "Hide items" : "View items"}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 whitespace-nowrap">
                    <Eye className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{expanded ? "Hide items" : "View items"}</span><span className="sm:hidden">{expanded ? "Hide" : "View"}</span>
                  </button>
                  <button
                    onClick={() => (addItemForSupplier === s.id && !editingItemId) ? closeItemForm() : openAddItem(s.id)}
                    disabled={!hasToken}
                    data-testid={`add-item-btn-${s.id}`}
                    title={hasToken ? "Add an item to this supplier" : "Sign in as admin to add items"}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50 whitespace-nowrap" style={{ background: TEAL }}>
                    <Plus className="h-3 w-3" /> Item
                  </button>
                  <button
                    onClick={() => openEditSupplier(s)}
                    disabled={!hasToken}
                    data-testid={`edit-supplier-btn-${s.id}`}
                    title={hasToken ? "Edit supplier details" : "Sign in as admin to edit"}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md text-xs border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
                    style={{ color: NAVY }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={async () => {
                      if (!deleteSupplier) return;
                      if (!window.confirm(`Remove supplier "${s.company}"?\n\nThis cannot be undone.\nAll items must be deleted first.`)) return;
                      try { await deleteSupplier(s.id); } catch (e) { alert(e.message); }
                    }}
                    disabled={!hasToken}
                    data-testid={`delete-supplier-btn-${s.id}`}
                    title={hasToken ? "Remove supplier" : "Sign in as admin to remove"}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50 whitespace-nowrap">
                    <Trash2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Remove</span>
                  </button>
                </div>
              </div>

              {/* Add / edit item form — rendered inline directly below
                  the selected supplier so the user sees exactly which
                  supplier they're adding an item to. */}
              {showItemFormHere && (
                <div className="border-t-2 p-3 sm:p-4" style={{ borderColor: TEAL, background: `${TEAL}08` }} data-testid="item-form-panel">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: NAVY }}>
                        {isEdit ? "Edit item" : "Add item"}
                      </p>
                      <p className="text-sm font-semibold" style={{ color: NAVY }}>
                        {isEdit ? `${itemForm.name || "—"}` : `for ${s.company}`}
                      </p>
                    </div>
                    <button onClick={closeItemForm} className="h-7 w-7 rounded-md text-slate-400 hover:bg-white flex items-center justify-center" aria-label="Close item form">
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {isEdit && (
                    <label className="block mb-3">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Supplier</span>
                      <select
                        value={addItemForSupplier}
                        onChange={(e) => setAddItemForSupplier(e.target.value)}
                        data-testid="item-supplier-select"
                        className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white">
                        {SUPPLIERS.map((sup) => (
                          <option key={sup.id} value={sup.id}>{sup.company}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_170px_1fr_170px] gap-2 mb-3">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Item name *</span>
                      <input value={itemForm.name} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. HCV Rapid Test Kit" data-testid="item-name-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
                    </label>
                    <label className="block relative">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</span>
                      <button
                        type="button"
                        onClick={() => setManageCatsOpen((v) => !v)}
                        data-testid="edit-categories-btn"
                        className="absolute top-0 right-0 text-[10px] normal-case tracking-normal text-teal-700 hover:underline font-medium"
                      >
                        {manageCatsOpen ? "Done" : "Edit list"}
                      </button>
                      <input
                        list="hepcure-inv-categories"
                        value={itemForm.category}
                        onChange={(e) => setItemForm((f) => ({ ...f, category: e.target.value }))}
                        placeholder="Pick or type"
                        data-testid="item-category-input"
                        className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                      />
                      <datalist id="hepcure-inv-categories">
                        {categoryOptions.map((c) => <option key={c} value={c} />)}
                      </datalist>
                      {manageCatsOpen && (
                        <div className="absolute z-20 top-full left-0 right-0 sm:right-auto sm:min-w-[220px] mt-1 rounded-md border border-slate-200 bg-white shadow-lg p-2" data-testid="manage-categories-popover">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Categories</p>
                          <ul className="max-h-40 overflow-y-auto space-y-1 mb-2">
                            {categoryOptions.length === 0 && (
                              <li className="text-xs text-slate-400 italic">No categories yet.</li>
                            )}
                            {categoryOptions.map((c) => (
                              <li key={c} className="flex items-center justify-between gap-2 text-xs">
                                {editingCatName === c ? (
                                  <>
                                    <input
                                      type="text"
                                      autoFocus
                                      value={editingCatDraft}
                                      onChange={(e) => setEditingCatDraft(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") { e.preventDefault(); saveEditCategory(); }
                                        else if (e.key === "Escape") { e.preventDefault(); cancelEditCategory(); }
                                      }}
                                      data-testid={`rename-category-input-${c}`}
                                      className="flex-1 h-6 rounded border border-slate-300 px-1.5 text-xs"
                                    />
                                    <button
                                      type="button"
                                      onClick={saveEditCategory}
                                      data-testid={`save-category-${c}`}
                                      className="text-[10px] font-semibold text-teal-700 hover:underline"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEditCategory}
                                      data-testid={`cancel-category-${c}`}
                                      className="text-[10px] font-semibold text-slate-500 hover:underline"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-1 min-w-0 truncate" style={{ color: NAVY }}>{c}</span>
                                    <button
                                      type="button"
                                      onClick={() => startEditCategory(c)}
                                      data-testid={`rename-category-${c}`}
                                      className="inline-flex items-center justify-center h-5 w-5 rounded text-slate-500 hover:bg-slate-100"
                                      title={`Rename ${c}`}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeCategory(c)}
                                      data-testid={`remove-category-${c}`}
                                      className="inline-flex items-center justify-center h-5 w-5 rounded text-red-600 hover:bg-red-50"
                                      title={`Remove ${c}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={newCatInput}
                              onChange={(e) => setNewCatInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                              placeholder="New category"
                              data-testid="new-category-input"
                              className="flex-1 h-8 rounded-md border border-slate-300 px-2 text-xs"
                            />
                            <button
                              type="button"
                              onClick={addCategory}
                              data-testid="add-category-btn"
                              className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-xs font-semibold text-white bg-teal-700 hover:bg-teal-800"
                            >
                              <Plus className="h-3 w-3" /> Add
                            </button>
                          </div>
                        </div>
                      )}
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</span>
                      <input value={itemForm.description} onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional — shown on PO only" data-testid="item-description-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Code / SKU</span>
                      <input value={itemForm.code} onChange={(e) => setItemForm((f) => ({ ...f, code: e.target.value }))} placeholder="Optional" data-testid="item-code-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm font-mono" />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-md border border-slate-200 bg-white p-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Pack size &amp; order</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Unit</span>
                          <select value={itemForm.pack} onChange={(e) => setItemForm((f) => ({ ...f, pack: e.target.value }))} data-testid="item-pack-select" className="h-8 w-full rounded-md border border-slate-300 px-1.5 text-xs">
                            <option>Each</option>
                            <option>Box</option>
                            <option>Pack</option>
                          </select>
                        </label>
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Qty</span>
                          <input type="number" min="1" value={itemForm.packQty} onChange={(e) => setItemForm((f) => ({ ...f, packQty: Number(e.target.value) }))} data-testid="item-units-input" className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Order</span>
                          <input type="number" min="1" value={itemForm.orderPacks} onChange={(e) => setItemForm((f) => ({ ...f, orderPacks: Number(e.target.value) }))} data-testid="item-orderpacks-input" className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                      </div>
                      <p className="mt-1 text-[10px] text-slate-500 tabular-nums text-right">
                        {itemForm.orderPacks || 0} × {itemForm.packQty || 0} = <b className="text-slate-700">{(itemForm.orderPacks || 0) * (itemForm.packQty || 0)}</b> units
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Team level (per outreach) — packs</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Min (packs)</span>
                          <input type="number" min="0" value={itemForm.min} onChange={(e) => setItemForm((f) => ({ ...f, min: Number(e.target.value) }))} className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Max (packs)</span>
                          <input type="number" min="0" value={itemForm.max} onChange={(e) => setItemForm((f) => ({ ...f, max: Number(e.target.value) }))} className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">HQ warehouse level — packs</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Min (packs)</span>
                          <input type="number" min="0" value={itemForm.hqMin} onChange={(e) => setItemForm((f) => ({ ...f, hqMin: Number(e.target.value) }))} className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                        <label className="block">
                          <span className="block text-[10px] text-slate-500 mb-0.5">Max (packs)</span>
                          <input type="number" min="0" value={itemForm.hqMax} onChange={(e) => setItemForm((f) => ({ ...f, hqMax: Number(e.target.value) }))} className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs text-right tabular-nums" />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex justify-end gap-2 items-center flex-wrap">
                    {itemErr && <p className="text-xs text-red-600 mr-auto" data-testid="item-form-err">{itemErr}</p>}
                    <button onClick={closeItemForm} className="h-8 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600">Cancel</button>
                    <button onClick={saveItem} disabled={itemBusy} data-testid="save-item-btn" className="h-8 px-4 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                      {itemBusy ? "Saving…" : (isEdit ? "Update item" : "Save item")}
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded — items list */}
              {expanded && (
                <div className="border-t border-slate-200 bg-slate-50 p-3 sm:p-4">
                  {items.length === 0 ? (
                    <p className="text-xs text-slate-500 italic py-2">No items yet — use the <b>+ Item</b> button above to add one.</p>
                  ) : (
                    <ul className="space-y-2" data-testid={`items-list-${s.id}`}>
                      {items.map((it) => (
                        <li key={it.id} className="rounded-md border border-slate-200 bg-white p-2.5 flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: NAVY }}>{it.name}</p>
                            <p className="text-[11px] text-slate-500 tabular-nums mt-0.5 leading-snug">
                              <span className="block sm:inline">
                                {it.code ? <span className="font-mono">{it.code} · </span> : null}
                                {(it.orderPacks && it.orderPacks > 1)
                                  ? <>{it.orderPacks} × {it.packQty} = <b className="text-slate-700">{it.orderPacks * it.packQty}</b></>
                                  : <>Units {it.packQty}</>
                                }
                              </span>
                              <span className="hidden sm:inline mx-1.5 text-slate-300">|</span>
                              <span className="block sm:inline">
                                Team {it.min}/{it.max} <span className="text-slate-400">packs</span>
                                <span className="mx-1.5 text-slate-300">|</span>
                                HQ {it.hqMin}/{it.hqMax} <span className="text-slate-400">packs</span>
                              </span>
                            </p>
                          </div>
                          <button
                            onClick={() => openEditItem(it)}
                            disabled={!hasToken}
                            data-testid={`edit-item-btn-${it.id}`}
                            title={hasToken ? "Edit this item" : "Sign in as admin to edit"}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-xs border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 shrink-0"
                            style={{ color: NAVY }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={async () => {
                              if (!deleteItem) return;
                              if (!window.confirm(`Delete item "${it.name}"?\n\nRemoves it from HQ and every team's stock. Cannot be undone.`)) return;
                              try { await deleteItem(it.id); } catch (e) { alert(e.message); }
                            }}
                            disabled={!hasToken}
                            data-testid={`delete-item-btn-${it.id}`}
                            title={hasToken ? "Delete this item" : "Sign in as admin to delete"}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-xs text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50 shrink-0">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add / edit item form is now rendered INLINE inside the target
          supplier card above, so it always appears directly below the
          supplier the admin picked. */}


      {/* ─────────────────────────────────────────────
          Danger zone — wipe inventory data for a fresh
          production start. Only reachable when signed
          in as admin (AdminSuppliers is admin-gated).
          ───────────────────────────────────────────── */}
      <div className="mt-8 pt-5 border-t-2 border-dashed border-red-200" data-testid="inventory-danger-zone">
        <div className="rounded-lg border border-red-300 bg-red-50/60 p-4">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-red-700">Danger zone</p>
            <span className="text-red-300 hidden sm:inline">·</span>
            <p className="text-sm font-semibold text-red-800">Clear all inventory data</p>
          </div>
          <div className="mt-1 flex items-start gap-3 flex-wrap sm:flex-nowrap">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-red-700/80 leading-relaxed">
                Deletes every supplier, item, HQ&nbsp;stock, team&nbsp;stock, request, receipt, purchase&nbsp;order,
                adjustment and monthly cycle in this environment.
              </p>
              <p className="text-xs text-red-700/80 leading-relaxed mt-1 sm:whitespace-nowrap">
                Demo data will <b>not</b> be re-seeded on restart. Use this once when going live.
              </p>
              {clearDone && (
                <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="clear-all-done-msg">
                  ✓ Inventory cleared. Warehouse is now empty.
                </p>
              )}
            </div>
            {!clearOpen && (
              <button
                onClick={() => { setClearOpen(true); setClearErr(""); setClearConfirm(""); }}
                disabled={!hasToken}
                data-testid="clear-all-demo-data-btn"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 shrink-0 shadow-sm self-start"
              >
                <Trash2 className="h-4 w-4" /> Clear all demo data
              </button>
            )}
          </div>

          {clearOpen && (
            <div className="mt-3 rounded-md bg-white border border-red-300 p-3">
              <p className="text-xs text-red-800 font-semibold mb-2">
                Type <span className="font-mono px-1 py-0.5 rounded bg-red-100">WIPE</span> to confirm.
                This cannot be undone.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  autoFocus
                  value={clearConfirm}
                  onChange={(e) => setClearConfirm(e.target.value)}
                  placeholder="Type WIPE"
                  data-testid="clear-all-confirm-input"
                  className="h-9 rounded-md border border-red-400 px-2 text-sm font-mono uppercase flex-1 min-w-[140px]"
                />
                <button
                  onClick={runClearAll}
                  disabled={clearBusy || clearConfirm.trim().toUpperCase() !== "WIPE"}
                  data-testid="clear-all-confirm-btn"
                  className="h-9 px-4 rounded-md text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 shadow-sm"
                >
                  {clearBusy ? "Wiping…" : "Wipe inventory"}
                </button>
                <button
                  onClick={() => { setClearOpen(false); setClearConfirm(""); setClearErr(""); }}
                  disabled={clearBusy}
                  className="h-9 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
              {clearErr && (
                <p className="mt-2 text-xs text-red-700" data-testid="clear-all-err">{clearErr}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
